import React, { FC } from 'react';
import { useAppSelector } from '../../hooks/redux-hooks';
import { selectBookingMedicareCardData, selectBookingSpecialist } from '../../redux/slices/booking';
import { MedicareCard } from '../../model/MedicareCard';
import {
  FeeBasis, FeeEstimate, formatMoney, formatRange, getFeeEstimate,
} from '../../utils/consultation-fee';

const BASIS_LABELS: Record<FeeBasis, string> = {
  'medicare-assumed': 'Medicare assumed',
  medicare: 'Medicare',
  'medicare-to-follow': 'Medicare (card to follow)',
  dva: 'DVA',
  none: 'No rebate',
};

const MEDICARE_NOTE = 'This amount is an estimate. The Medicare item number is decided by your clinician at the end of the consultation, based on the type of consultation you have. If it changes your rebate, our admin team will contact you to confirm the final amount with you.';
const NOTES: Record<FeeBasis, string> = {
  'medicare-assumed': MEDICARE_NOTE,
  medicare: MEDICARE_NOTE,
  'medicare-to-follow': MEDICARE_NOTE,
  dva: MEDICARE_NOTE,
  none: 'Without a Medicare or DVA card no rebate applies, so the out-of-pocket amount is the full consultation fee.',
};

const CLAIM_NOTE = 'We lodge your Medicare claim within 3 business days of you attending your appointment. Medicare pays any rebate straight into your nominated bank account — the rebate may vary based on your eligibility.';

// The estimate for the chosen clinician and cover; Medicare is assumed until cover is chosen.
export const useFeeEstimate = (): FeeEstimate | null => {
  const specialist = useAppSelector(selectBookingSpecialist);
  const { coverType } = useAppSelector(selectBookingMedicareCardData) as Partial<MedicareCard>;
  return getFeeEstimate(specialist?.fee?.initial, coverType);
};

const FeeCard: FC = () => {
  const specialist = useAppSelector(selectBookingSpecialist);
  const estimate = useFeeEstimate();

  if (!specialist || !estimate) {
    return (
      <section className="fee" aria-label="Consultation fees">
        <div className="hd">Consultation fees</div>
        <div className="ft">
          <p>
            {specialist
              ? 'Our admin team will confirm the fee with you before your appointment.'
              : 'Choose a clinician to see their fee.'}
          </p>
        </div>
      </section>
    );
  }

  const {
    basis, total, rebate, gap,
  } = estimate;
  // Medicare and DVA both claim the rebate.
  const hasRebate = basis !== 'none' && !!rebate;

  return (
    <section className="fee" aria-label="Consultation fees">
      <div className="hd">
        Consultation fees
        <span className="tag tag-new">{BASIS_LABELS[basis]}</span>
      </div>
      <div className="rows">
        <div className="frow">
          <span className="k">Total fee</span>
          <span className="v">{formatMoney(total)}</span>
        </div>
        {hasRebate && rebate && (
          <div className="frow">
            <span className="k">
              <b>{basis === 'dva' ? 'DVA rebate' : 'Medicare rebate'}</b>
              May vary based on your eligibility
            </span>
            <span className="v">{formatRange(rebate)}</span>
          </div>
        )}
        <div className="frow total">
          <span className="k">
            <b>
              Out of pocket (gap fee)
              {hasRebate && <span className="est">Estimate</span>}
            </b>
          </span>
          <span className="v">{formatRange(gap)}</span>
        </div>
      </div>
      <div className="ft">
        <p>{NOTES[basis]}</p>
        {hasRebate && <p>{CLAIM_NOTE}</p>}
      </div>
    </section>
  );
};

export default FeeCard;
